/**
 * The app shell.
 *
 * Rebuilt to match the BlueSphere portal's shell, because the previous
 * left-sidebar layout was the single biggest reason this product did not
 * read as part of the family. BlueSphere has no sidebar anywhere — the
 * portal is a sticky topbar with pill navigation over a navy gradient
 * canvas, and it keeps unused sidebar scaffolding precisely because that
 * decision was made deliberately.
 *
 * Conventions taken from `bluesphere-portal/components/navbar.tsx`:
 *   - sticky header on the gradient, logo left, pill nav beside it
 *   - active pill is white-on-navy; inactive is translucent
 *   - the account cluster sits right, pushed by `ml-auto`
 *   - page content is full-bleed with responsive gutters, no max-width;
 *     the CARD carries the title, not a page header bar
 *
 * The tabs read along the pipeline (task 158): Overview · Integrations ·
 * Events · Detections · Alerts · Clients — what we connect to leads, then
 * what came in; a Playbooks tab goes between Detections and Alerts when the
 * editor exists. Tickets live in bluesphere-ticketing (task 193). The
 * active tab is decided here from the
 * router state rather than by TanStack's `activeProps`.
 *
 * Two rows since 2026-10-08 (task 201). One row held nine pills, a
 * two-line identity block, a native Client select and a Sign out button,
 * which fit above about 1600px and scrolled sideways everywhere else:
 *
 *   - Row one: logo, the sections (`SectionNav`, which measures and folds
 *     what does not fit into a More menu rather than scrolling), and the
 *     account behind an avatar (`AccountMenu`).
 *   - Row two: the Client strip of task 60, promoted into the header and
 *     made the switcher itself (`ClientSwitcher`): one control, the same
 *     place on every page, saying which Client the screen is showing.
 *     The section name sits at its right as a breadcrumb.
 *
 * Below `md` the pill nav has no room (task 74): row one is logo and a
 * menu button, and the panel under the header carries the sections, who
 * is signed in, and Sign out. Row two stays, so the Client is visible
 * without opening the menu. The panel closes on navigation and on Escape.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from 'react-oidc-context';

import { signOut as identitySignOut } from '@bluesphere/identity-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ClientSwitcher } from './ClientSwitcher';
import { Logo } from './Logo';
import { NewAlertsBeacon, useNewAlerts } from './NewAlertsBeacon';
import { AccountMenu } from './nav/AccountMenu';
import { SectionNav, isActiveSection, type SectionTab } from './nav/SectionNav';
import { identityConfig } from '../auth/oidc';
import { useOrgAdmin } from '../auth/useOrgAdmin';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { clientsUnder, orgLabel } from '../lib/orgs';
import { roleLine as roleLineFor } from '../lib/users';

interface LayoutProps {
  children: ReactNode;
}

/**
 * In pipeline order: what we connect to, what comes in, what was noticed,
 * what needs a person; then who we do it for (task 69; reordered
 * 2026-09-29, task 158).
 */
const TABS: SectionTab[] = [
  { to: '/', label: 'Overview', exact: true },
  { to: '/integrations', label: 'Integrations' },
  { to: '/events', label: 'Events' },
  { to: '/detections', label: 'Detections' },
  { to: '/alerts', label: 'Alerts' },
  { to: '/organizations', label: 'Organizations' },
];

/** Shown only to a caller holding `manage_org` (Story 22; Story 48). */
const USERS_TAB: SectionTab = { to: '/users', label: 'Users' };

/** On the roadmap, not in the product: greyed in the More menu, never a pill. */
const SOON = ['Investigation', 'Settings'];

const SECTION_TITLES: Record<string, string> = {
  '/': 'Overview',
  '/detections': 'Detections',
  '/alerts': 'Alerts',
  '/events': 'Events',
  '/organizations': 'Organizations',
  '/integrations': 'Integrations',
  '/users': 'Users',
  '/preferences': 'Preferences',
};

/**
 * The sections that are about every Client at once, not the selected one.
 * Users are Simulint staff, not any one Client's; Preferences cover every
 * Client in the caller's scope.
 */
const ALL_CLIENTS_SECTIONS = new Set([
  '/organizations',
  '/integrations',
  '/users',
  '/preferences',
]);

/** With no Client selected these show every Client rather than asking for one. */
const CROSS_CLIENT_SECTIONS = new Set(['/', '/alerts', '/detections']);

export function Layout({ children }: LayoutProps) {
  const auth = useAuth();
  const tenant = useTenant();
  const { selected, tenantId, selectedOrg, orgs } = useSelectedTenant();
  const roleLine = (me: NonNullable<typeof tenant.me>) =>
    roleLineFor(me, orgs.homeId ? clientsUnder(orgs, orgs.homeId) : undefined);
  const orgAdmin = useOrgAdmin();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const newAlerts = useNewAlerts();

  const section = sectionOf(pathname);
  const sectionTitle = SECTION_TITLES[section] ?? '';

  // The browser tab says where you are and for whom (task 60), prefixed
  // with the unseen-alert count while there is one (Story 25) — the badge
  // a throttled background tab still shows.
  useEffect(() => {
    const title = sectionTitle || 'BlueSphere MDR';
    const badge = newAlerts.count > 0 ? `(${newAlerts.label}) ` : '';
    document.title = selected
      ? `${badge}${title} · ${selected.display_name} · BlueSphere MDR`
      : `${badge}${title} · BlueSphere MDR`;
  }, [sectionTitle, selected, newAlerts.count, newAlerts.label]);

  // Navigating is how the menu is used; arriving closes it.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // Sign-out is global (Story 53): POST /sign-out with our token ends the
  // session in every app, then managed login's /logout.
  const signOut = () =>
    identitySignOut(() => auth.removeUser(), identityConfig, 'user', {
      accessToken: auth.user?.access_token,
    });

  // The Alerts pill wears the unseen count (Story 25).
  const tabs = (orgAdmin.holds ? [...TABS, USERS_TAB] : TABS).map((t) =>
    t.to === '/alerts' && newAlerts.count > 0 ? { ...t, badge: newAlerts.label } : t,
  );

  return (
    <div className="min-h-screen pb-6 text-white">
      <header className="sticky top-0 z-50 w-full bg-gradient">
        {/* Row one: brand, sections, account. */}
        <div className="flex h-16 items-center gap-2 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="BlueSphere MDR">
            <Logo />
            {/* The product name, not the brand: the logo already says
                BlueSphere, and repeating it was what made the header read as
                a placeholder. */}
            <span className="hidden text-sm font-medium text-white/70 sm:inline">MDR</span>
          </Link>

          <div className="hidden min-w-0 flex-1 md:flex">
            <SectionNav tabs={tabs} soon={SOON} pathname={pathname} />
          </div>

          <div className="ml-auto hidden shrink-0 items-center md:flex">
            <AccountMenu
              email={tenant.email}
              me={tenant.me}
              roleLine={tenant.me ? roleLine(tenant.me) : null}
              onSignOut={() => void signOut()}
              pathname={pathname}
            />
          </div>

          {/* Mobile: one button, one panel. */}
          <button
            type="button"
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 md:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <MenuIcon open={menuOpen} />
          </button>
        </div>

        {/* Row two: the Client context (task 60), the same place on every
            page. Clients, Integrations, Users and Preferences are the pages
            that are about all of them. */}
        <div className="flex h-11 min-w-0 items-center gap-3 border-t border-white/10 px-4 md:px-6">
          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-widest text-white/50">
            {orgs.homeId ? 'Viewing' : 'Client'}
          </span>
          <ClientSwitcher
            allClientsPage={ALL_CLIENTS_SECTIONS.has(section)}
            hint={
              ALL_CLIENTS_SECTIONS.has(section)
                ? 'this page covers every Client'
                : !tenantId && !selectedOrg && CROSS_CLIENT_SECTIONS.has(section)
                  ? 'showing all Clients'
                  : null
            }
          />
          {sectionTitle && (
            <span className="ml-auto hidden min-w-0 truncate text-sm text-white/50 md:inline">
              {(selected || selectedOrg) && !ALL_CLIENTS_SECTIONS.has(section) ? (
                <>
                  {selected ? selected.display_name : `All of ${orgLabel(selectedOrg)}`}{' '}
                  <span className="opacity-60">/</span>{' '}
                </>
              ) : null}
              <span className="font-medium text-white">{sectionTitle}</span>
            </span>
          )}
        </div>

        {menuOpen && (
          <div
            id="mobile-menu"
            className="absolute inset-x-0 top-full z-50 border-t border-white/10 bg-bluesphere-bg px-4 pb-5 pt-4 shadow-modal md:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Sections">
              {tabs.map((tab) => (
                <DrawerPill
                  key={tab.to}
                  tab={tab}
                  active={isActiveSection(pathname, tab.to, tab.exact)}
                />
              ))}
              {SOON.map((label) => (
                <span
                  key={label}
                  aria-disabled="true"
                  className="flex h-11 cursor-not-allowed items-center justify-between rounded-full bg-white/5 px-4 text-sm font-medium text-white/35"
                >
                  {label}
                  <span className="text-[10px] uppercase tracking-wider">soon</span>
                </span>
              ))}
            </nav>
            <div className="mt-4 flex items-center justify-between gap-4 border-t border-white/10 pt-4">
              <Link to="/preferences" className="group block min-w-0 rounded-lg">
                <div className="truncate text-sm leading-tight text-white group-hover:underline">
                  {tenant.email || 'unknown user'}
                </div>
                {tenant.me && (
                  <div className="text-xs leading-tight text-white/60">
                    {roleLine(tenant.me)}
                    {' · Preferences'}
                  </div>
                )}
              </Link>
              <PillButton variant="secondary" size="sm" onClick={() => void signOut()}>
                Sign out
              </PillButton>
            </div>
          </div>
        )}
      </header>

      {/* Full-bleed with responsive gutters, matching the portal's
          dashboards. No max-width: the widget grid does the constraining.
          The track is minmax(0, 1fr), not auto: with auto, a child whose
          minimum content width exceeds the viewport (a row of nowrap pills)
          widens the page instead of scrolling inside its own box, and the
          whole screen scrolls sideways on a phone. */}
      <div className="mt-5 grid grid-cols-[minmax(0,1fr)] gap-6 px-6 md:px-8 xl:px-12">
        {children}
      </div>

      <NewAlertsBeacon alerts={newAlerts} />
    </div>
  );
}

function sectionOf(pathname: string): string {
  const first = '/' + (pathname.split('/')[1] ?? '');
  return first === '/' ? '/' : first;
}

/** A section in the mobile panel: a 44px row, the active one white. */
function DrawerPill({ tab, active }: { tab: SectionTab; active: boolean }) {
  const base = 'flex h-11 items-center justify-between rounded-full px-4 text-sm transition-colors';
  const state = active
    ? 'bg-white font-semibold text-bluesphere-primary shadow-md'
    : 'bg-white/10 font-medium text-white hover:bg-white/20';
  return (
    <Link to={tab.to} className={`${base} ${state}`} aria-current={active ? 'page' : undefined}>
      {tab.label}
      {tab.badge && (
        <span className="rounded-full bg-bluesphere-scoreCritical px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
          {tab.badge}
        </span>
      )}
    </Link>
  );
}

/** Three lines, or a cross while the menu is open. */
function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      {open ? (
        <>
          <line x1="4" y1="4" x2="16" y2="16" />
          <line x1="16" y1="4" x2="4" y2="16" />
        </>
      ) : (
        <>
          <line x1="3" y1="5" x2="17" y2="5" />
          <line x1="3" y1="10" x2="17" y2="10" />
          <line x1="3" y1="15" x2="17" y2="15" />
        </>
      )}
    </svg>
  );
}
